import React from "react";
import { Link } from "react-router-dom";
import { Coins, Building2, Briefcase, FileText, Calendar, CreditCard, Computer, Sparkles, ArrowRight, Phone, Mail, Truck, Scissors, Forklift, Archive } from "lucide-react";
import { PageHero, Section, Card } from "@/components/Section";
import { IMAGES, BRAND } from "@/lib/site";

const objectifs = [
  { icon: Briefcase, t: "Développer nos formations professionnelles", d: "Continuer à proposer des cursus en phase avec les besoins du tissu économique angevin." },
  { icon: Computer, t: "Moderniser les équipements", d: "Renouveler nos plateaux techniques industriels et informatiques pour nos élèves." },
  { icon: Sparkles, t: "Soutenir les projets des élèves", d: "Financer leurs initiatives pédagogiques, culturelles et solidaires tout au long de l'année." },
];

const etapes = [
  {
    n: "1",
    titre: "Déclarer & payer",
    items: [
      "Déclarez la taxe d'apprentissage dans votre DSN d'avril (code CTP 995 pour le solde).",
      "Payez le solde (0,09 % de la masse salariale) à l'URSSAF ou à la MSA avant le 5 ou le 15 mai.",
    ],
    icon: FileText,
  },
  {
    n: "2",
    titre: "Se connecter à SOLTÉA",
    items: [
      "À partir de fin mai, connectez-vous à la plateforme SOLTÉA via vos identifiants Net-Entreprises.",
    ],
    icon: CreditCard,
  },
  {
    n: "3",
    titre: "Choisir le lycée bénéficiaire",
    items: [
      "Recherchez : Lycée Professionnel Joseph Wresinski — Angers",
      "Code UAI : 0492432C · SIRET : 786 117 317 00051",
      "Sélectionnez-le et indiquez le montant ou le pourcentage à attribuer.",
    ],
    icon: Building2,
  },
  {
    n: "4",
    titre: "Valider la répartition",
    items: [
      "Validez votre choix sur SOLTÉA avant la clôture de la campagne.",
      "La Caisse des Dépôts versera ensuite directement les fonds au lycée.",
    ],
    icon: Calendar,
  },
];

const projets = [
  { icon: Scissors, t: "Machines à coudre", d: "Pour nos ateliers textile et CAP Métiers de la Mode." },
  { icon: Truck, t: "Camion auto-école", d: "Pour former nos futurs conducteurs Bac Pro CTRM." },
  { icon: Forklift, t: "Gerbeur CACES 1B", d: "Pour nos formations logistique et manutention." },
  { icon: Archive, t: "Nouveaux casiers élèves", d: "Pour un quotidien plus pratique et plus serein au lycée." },
];

export default function TaxeApprentissage() {
  return (
    <div data-testid="taxe-apprentissage-page">
      <PageHero
        eyebrow="Espace entreprises · Taxe d'apprentissage"
        title="Soutenez le lycée Joseph Wresinski grâce à la taxe d'apprentissage"
        subtitle="L'affectation du solde de la taxe d'apprentissage se fait via la plateforme SOLTÉA. Vous pouvez désigner directement notre lycée comme bénéficiaire et contribuer ainsi à former les jeunes aux métiers d'aujourd'hui et de demain."
        image={IMAGES.workshop}
      >
        <div className="mt-2 flex flex-wrap gap-3">
          <a
            href="https://www.soltea.education.gouv.fr"
            target="_blank"
            rel="noreferrer"
            data-testid="taxe-cta-soltea"
            className="inline-flex items-center gap-2 rounded-full bg-brand-sand-dark px-5 py-3 text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-brand-sand"
          >
            Affecter la taxe sur SOLTÉA
            <ArrowRight className="h-4 w-4" />
          </a>
          <a
            href="mailto:adjointdegestion@lpjw.fr"
            data-testid="taxe-cta-mail"
            className="inline-flex items-center gap-2 rounded-full border border-brand-cream/30 px-5 py-3 text-sm font-semibold text-brand-cream transition-all duration-300 hover:bg-brand-cream hover:text-brand-slate-dark"
          >
            Une question ? Nous écrire
          </a>
        </div>
      </PageHero>

      {/* Encadré références */}
      <Section>
        <div className="rounded-3xl border-2 border-brand-sand bg-brand-sand-wrapper/40 p-8 lg:p-12">
          <div className="flex flex-col items-start gap-6 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.18em] text-brand-sand-dark">
                Nos références à indiquer sur SOLTÉA
              </p>
              <p className="mt-3 font-heading text-2xl font-bold text-brand-slate-dark sm:text-3xl">
                Lycée Professionnel Joseph Wresinski — Angers
              </p>
            </div>
            <div className="grid w-full gap-4 sm:grid-cols-2 lg:w-auto">
              <div className="rounded-2xl bg-brand-cream p-5">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Code UAI</p>
                <p data-testid="taxe-uai" className="mt-2 font-heading text-xl font-bold tabular-nums text-brand-slate-dark">
                  {BRAND.codeUAI}
                </p>
              </div>
              <div className="rounded-2xl bg-brand-cream p-5">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">SIRET</p>
                <p data-testid="taxe-siret" className="mt-2 font-heading text-xl font-bold tabular-nums text-brand-slate-dark">
                  {BRAND.siret}
                </p>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Objectifs du soutien */}
      <Section eyebrow="À quoi sert votre contribution ?" title="Trois objectifs concrets pour nos élèves">
        <div className="grid gap-6 md:grid-cols-3">
          {objectifs.map((o, i) => (
            <Card key={o.t} data-testid={`taxe-objectif-${i}`}>
              <span className="grid h-12 w-12 place-items-center rounded-2xl bg-brand-slate/10 text-brand-slate-dark">
                <o.icon className="h-5 w-5" />
              </span>
              <h3 className="mt-5 font-heading text-lg font-semibold text-brand-slate-dark">{o.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{o.d}</p>
            </Card>
          ))}
        </div>
        <p className="mt-8 text-base leading-relaxed text-muted-foreground">
          Grâce à votre soutien, nous préparons nos jeunes aux métiers d'aujourd'hui et de demain.
        </p>
      </Section>

      {/* 4 étapes */}
      <Section
        wrapper="bg-brand-slate-dark text-brand-cream"
        eyebrow="Mode d'emploi"
        title="Affecter la taxe en 4 étapes"
      >
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {etapes.map((e) => (
            <div
              key={e.n}
              data-testid={`taxe-etape-${e.n}`}
              className="relative flex flex-col rounded-3xl border border-white/10 bg-white/5 p-6"
            >
              <div className="flex items-center gap-3">
                <span className="grid h-10 w-10 place-items-center rounded-full bg-brand-sand text-brand-slate-dark font-heading text-base font-bold">
                  {e.n}
                </span>
                <e.icon className="h-5 w-5 text-brand-sand" />
              </div>
              <h3 className="mt-5 font-heading text-lg font-bold text-brand-cream">{e.titre}</h3>
              <ul className="mt-4 space-y-2.5 text-sm leading-relaxed text-brand-cream/85">
                {e.items.map((it) => (
                  <li key={it} className="flex gap-2">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-sand" />
                    {it}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-10 flex justify-center">
          <a
            href="https://www.soltea.education.gouv.fr"
            target="_blank"
            rel="noreferrer"
            data-testid="taxe-cta-soltea-bottom"
            className="inline-flex items-center gap-2 rounded-full bg-brand-sand-dark px-6 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-brand-sand"
          >
            Affecter la taxe sur SOLTÉA
            <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </Section>

      {/* Projets financés */}
      <Section
        eyebrow="Votre soutien transforme nos idées en actions !"
        title="Quelques projets financés grâce à la taxe d'apprentissage"
      >
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {projets.map((p, i) => (
            <Card key={p.t} data-testid={`taxe-projet-${i}`}>
              <span className="grid h-12 w-12 place-items-center rounded-2xl bg-brand-sand/30 text-brand-slate-dark">
                <p.icon className="h-5 w-5" />
              </span>
              <h3 className="mt-5 font-heading text-base font-semibold text-brand-slate-dark">{p.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{p.d}</p>
            </Card>
          ))}
        </div>
      </Section>

      {/* Contact */}
      <Section wrapper="bg-brand-sand-wrapper/40">
        <div className="rounded-3xl border border-black/5 bg-brand-cream p-10 lg:p-14">
          <div className="grid items-center gap-8 lg:grid-cols-[1.2fr_1fr]">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.18em] text-brand-sand-dark">
                Pour toute question
              </p>
              <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-brand-slate-dark sm:text-4xl">
                M. Éric SEBILLE
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">Adjoint de gestion</p>
              <div className="mt-6 space-y-3 text-sm">
                <a
                  href="mailto:adjointdegestion@lpjw.fr"
                  data-testid="taxe-contact-mail"
                  className="flex items-center gap-3 text-brand-slate-dark transition-colors hover:text-brand-sand-dark"
                >
                  <Mail className="h-4 w-4 text-brand-sand-dark" />
                  adjointdegestion@lpjw.fr
                </a>
                <a
                  href="tel:0252094109"
                  data-testid="taxe-contact-tel"
                  className="flex items-center gap-3 text-brand-slate-dark transition-colors hover:text-brand-sand-dark"
                >
                  <Phone className="h-4 w-4 text-brand-sand-dark" />
                  02 52 09 41 09
                </a>
              </div>
            </div>
            <div className="flex flex-wrap gap-3 lg:justify-end">
              <Link
                to="/entreprises/devenir-partenaire"
                data-testid="taxe-cta-partenaire"
                className="inline-flex items-center gap-2 rounded-full bg-brand-slate px-6 py-3.5 text-sm font-semibold text-brand-cream transition-all duration-300 hover:-translate-y-0.5 hover:bg-brand-slate-dark"
              >
                Devenir partenaire
              </Link>
              <Link
                to="/entreprises/bureau"
                data-testid="taxe-cta-bureau"
                className="inline-flex items-center gap-2 rounded-full border border-brand-slate/20 px-6 py-3.5 text-sm font-semibold text-brand-slate-dark transition-all duration-300 hover:bg-brand-slate hover:text-brand-cream"
              >
                Bureau des entreprises
              </Link>
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
}
